import { apiClient } from '../api/axiosInstance';

/**
 * Coding reference-data service (feature 046).
 *
 * Backs the type-ahead on the CPT, ICD-10 and modifier inputs from the QB Adapter's local mirror
 * of the Quick Base code sets (`/api/v1/qb/codes`). The mirror is global — no tenant scoping — and
 * the adapter's read path never calls Quick Base, so a Quick Base outage cannot affect this.
 *
 * **Deliberately not cached, unlike `locationsService`.** Those fetch one whole list under one
 * stable key, which caches perfectly. A search endpoint is keyed by query string, so every
 * distinct prefix a biller types would become a permanent entry in `apiCache`'s unevicted `Map` —
 * a leak that grows with use. Worse, a `NOT_READY` response cached during the pre-sync window
 * would keep the field dark for the whole stale window after the sync had already finished.
 */

/** Which coding set to search. Matches the adapter's `CodeType` enum. */
export type CodeType = 'CPT' | 'ICD10' | 'MODIFIER';

/**
 * Shortest query that searches each set at all.
 *
 * Per-set, not global, because both reasons for having a floor scale with the set. A short prefix
 * against 74,727 ICD-10 rows is the least selective query the endpoint can be asked; against 406
 * modifiers selectivity is irrelevant and a scan is free.
 *
 * **Modifiers sit at 1 because every modifier code is exactly two characters.** A floor of 2 would
 * mean the biller had to know the whole code before the dropdown said anything, so the control
 * could only confirm a code, never help find one; a floor of 3 made the set uncompletable
 * entirely. Description matching still begins at 3 everywhere — a one-character substring matches
 * nearly every description, which is noise rather than an affordance.
 *
 * Mirrors `QuickBaseCodeTable.MinQueryLength` in the adapter, which enforces it server-side.
 */
export const MIN_QUERY_LENGTH_BY_TYPE: Record<CodeType, number> = {
  CPT: 3,
  ICD10: 3,
  MODIFIER: 1,
};

/** The floor for `type`, or the strictest floor when no set is selected. */
export function minQueryLengthFor(type: CodeType | undefined): number {
  return type ? MIN_QUERY_LENGTH_BY_TYPE[type] : 3;
}

/**
 * Whether the set can serve suggestions.
 *
 * `NOT_READY` is not an error and must never render as one: the set is either still loading on a
 * fresh release, or an operator has switched the feature off. Both degrade to plain free-text
 * entry. It is distinct from `READY` with zero codes, which means the query genuinely matched
 * nothing — telling a biller to "wait" for that would be a lie.
 */
export type CodeSearchStatus = 'READY' | 'NOT_READY';

export interface Code {
  code: string;
  description: string;
}

export interface CodeSearchResult {
  status: CodeSearchStatus;
  codes: Code[];
}

/** Wire shape returned by the adapter. */
export interface CodeSearchWire {
  status: CodeSearchStatus;
  codes: { code: string; description: string }[];
}

export interface SearchCodesOptions {
  /** Aborts an in-flight request when the biller types again. */
  signal?: AbortSignal;
  /** Server clamps to 50; omit for the server default of 20. */
  limit?: number;
}

/**
 * Search one coding set.
 *
 * Returns `NOT_READY` with no codes rather than throwing when the mirror is empty — the caller
 * falls back to free text, which is exactly the behaviour these fields had before this feature.
 */
export async function searchCodes(
  type: CodeType,
  query: string,
  options: SearchCodesOptions = {},
): Promise<CodeSearchResult> {
  const response = await apiClient.get<CodeSearchWire>('/api/v1/qb/codes', {
    params: {
      type,
      q: query,
      ...(options.limit !== undefined ? { limit: options.limit } : {}),
    },
    signal: options.signal,
  });

  const data = response.data;
  return {
    status: data?.status === 'READY' ? 'READY' : 'NOT_READY',
    codes: Array.isArray(data?.codes)
      ? data.codes.map((c) => ({ code: c.code, description: c.description ?? '' }))
      : [],
  };
}
